import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
    View,
    Image,
    Text,
    AppState,
    AppStateStatus,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Telemetry } from 'app/services/telemetry/Telemetry';
import { useAuth } from 'app/auth/hooks/useAuth';
import Button from '@shared/components/md-design-system/Button/Button';
import LoadingSpinner from '@shared/components/md-design-system/Layout/LoadingSpinner';
import { useTranslation } from 'react-i18next';


const SplashScreen = () => {
    const [showLoginButton, setShowLoginButton] = useState(false);
    const [isLoggingIn, setIsLoggingIn] = useState(false);
    const [authCheckCompleted, setAuthCheckCompleted] = useState(false);
    const { login, isLoading: authIsLoading } = useAuth();
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const { t } = useTranslation();


    const handleLogin = useCallback(async () => {
        try {
            setIsLoggingIn(true);
            setShowLoginButton(false);
            await login();
        } catch (error) {
            Telemetry.error(error as Error, {
                event: 'auth.login.failed',
                domain: 'auth',
                context: { source: 'SplashScreen' }
            });
            setIsLoggingIn(false);
            setShowLoginButton(true);
        }
    }, [login]);

    useEffect(() => {
        if (!authIsLoading && !authCheckCompleted) {
            setAuthCheckCompleted(true);
            setTimeout(() => {
                setShowLoginButton(true);
                setTimeout(() => {
                    handleLogin();
                }, 500);
            }, 500);
        }
    }, [authIsLoading, authCheckCompleted, handleLogin]);

    useEffect(() => {
        if (!isLoggingIn) {
            const timer = setTimeout(() => {
                setShowLoginButton(true);
            }, 1000);

            return () => clearTimeout(timer);
        }
    }, [isLoggingIn]);

    useEffect(() => {
        const handleAppStateChange = (nextAppState: AppStateStatus) => {
            if (nextAppState === 'active' && isLoggingIn) {
                if (timerRef.current) clearTimeout(timerRef.current);
                timerRef.current = setTimeout(() => {
                    setIsLoggingIn(false);
                    setShowLoginButton(true);
                }, 2000);
            }
        };

        const subscription = AppState.addEventListener('change', handleAppStateChange);

        return () => {
            subscription.remove();
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, [isLoggingIn]);

    return (
        <SafeAreaView className="flex-1 bg-primary-700">
            <View className="flex-1 justify-center items-center">
                <View className="w-full h-1/4 mb-8">
                    <Image
                        resizeMode="contain"
                        className="w-full h-full"
                        source={require('@assets/images/splash_logo.png')}
                    />
                </View>

                <View className="absolute bottom-16 w-full px-10">
                    {authIsLoading && !authCheckCompleted && (
                        <View className="items-center">
                            <LoadingSpinner size="medium" color="white" />
                        </View>
                    )}

                    {showLoginButton && !isLoggingIn && authCheckCompleted && (
                        <Button
                            variant="outline"
                            size="large"
                            onPress={handleLogin}
                        >
                            {t('screens.splash.login')}
                        </Button>
                    )}

                    {isLoggingIn && (
                        <View className="items-center">
                            <LoadingSpinner size="medium" color="white" />
                            <Text className="text-white text-base text-center mt-4">
                                {t('screens.splash.loggingIn')}
                            </Text>
                        </View>
                    )}
                </View>
            </View>
        </SafeAreaView>
    );
};

export default SplashScreen;
